import { useLayoutEffect, useMemo, useState } from 'react';

type UseLastAssistantMinHeightArgs = {
  scrollEl: HTMLElement | null;
  contentEl: HTMLElement | null;
  lastUserEl: HTMLElement | null;
};

type UseLastAssistantMinHeightResult = {
  minHeightPx: number | undefined;
};

function parsePx(value: string): number {
  // getComputedStyle() can return `normal` for gap and empty strings in some edge cases.
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function computeMinHeightPx(args: UseLastAssistantMinHeightArgs): number | undefined {
  const { scrollEl, contentEl, lastUserEl } = args;

  if (!scrollEl || !contentEl || !lastUserEl) return undefined;

  const styles = getComputedStyle(contentEl);
  const gapPx = parsePx(styles.rowGap || styles.gap);
  const paddingTopPx = parsePx(styles.paddingTop);
  const paddingBottomPx = parsePx(styles.paddingBottom);

  const minHeightPx = Math.max(
    0,
    scrollEl.clientHeight - paddingTopPx - lastUserEl.offsetHeight - gapPx - paddingBottomPx
  );
  return Number.isFinite(minHeightPx) ? minHeightPx : undefined;
}

/**
 * Computes a min-height to apply to the last assistant message so the immediately
 * preceding user message pins to the top of the scroll container while allowing
 * the assistant message to grow naturally.
 */
export function useLastAssistantMinHeight({
  scrollEl,
  contentEl,
  lastUserEl,
}: UseLastAssistantMinHeightArgs): UseLastAssistantMinHeightResult {
  const [minHeightPx, setMinHeightPx] = useState<number | undefined>(undefined);

  const args = useMemo(
    () => ({ scrollEl, contentEl, lastUserEl }),
    [contentEl, lastUserEl, scrollEl]
  );

  useLayoutEffect(() => {
    const recompute = () => {
      setMinHeightPx(computeMinHeightPx(args));
    };

    recompute();

    if (!args.scrollEl || !args.lastUserEl) return;

    const ro = new ResizeObserver(() => {
      recompute();
    });

    ro.observe(args.scrollEl);
    ro.observe(args.lastUserEl);

    return () => {
      ro.disconnect();
    };
  }, [args]);

  return { minHeightPx };
}
